<template>
  <div>
    <slot name="heading" :edit-button-attrs="editButtonAttrs">
      <AppTableHeader :left-cols="9" :right-cols="3">
        <template #left>
          {{
            $t("wallet:billingContacts.component.heading", "Billing contacts")
          }}
        </template>
        <template #right>
          <v-btn
            v-if="editButtonAttrs.renderable()"
            v-bind="editButtonAttrs"
            class="edit-green"
            small
            v-on="editButtonAttrs.on"
          >
            <v-icon color="white" small>$edit</v-icon>
          </v-btn>
        </template>
      </AppTableHeader>
    </slot>
    <v-col cols="12" :class="{ [contentPadding]: true }">
      <slot name="hint" :attrs="{ class: 'muted--text pb-0 mb-0' }">
        <p class="muted--text pb-0 mb-0">
          {{
            $t(
              "wallet:billingContacts.component.description",
              "The following billing contacts will be used to issue an invoice or receipt. "
            )
          }}
        </p>
      </slot>

      <v-progress-linear
        v-if="$apollo.queries.initBillingContactForm.loading"
        indeterminate
      />

      <template v-if="!editMode">
        <v-col v-if="!!contact" :cols="forceCols || 4" class="px-0">
          <div class="mb-2">
            <b>{{
              $t(
                "wallet:billingContacts.contactView.name.subheading",
                "Name of document receiver"
              )
            }}</b>
            <br />
            <template v-if="contact.organisationName">
              {{ contact.organisationName }}
            </template>
            <template v-else-if="contact.firstName || contact.lastName">
              {{ contact.firstName || "" }} {{ contact.lastName || "" }}
            </template>
          </div>

          <div v-if="addressContactFormat(contact)" class="mb-2">
            <b>{{
              $t(
                "wallet:billingContacts.contactView.address.subheading",
                "Billing address"
              )
            }}</b>
            <br />{{ addressContactFormat(contact) }}
          </div>

          <template v-if="contact.vatCode">
            <div class="mb-2">
              <b>{{
                $t(
                  "wallet:billingContacts.contactView.VATnumber.subheading",
                  "VAT number"
                )
              }}</b>
              <br />
              {{ contact.vatCode }}
            </div>
          </template>

          <template v-if="contact.billingEmail">
            <div class="mb-2">
              <b>{{
                $t(
                  "wallet:billingContacts.contactView.emailForBilling.subheading",
                  "E-mail for billing messages"
                )
              }}</b>

              <br />
              {{ contact.billingEmail }}
            </div>
          </template>
        </v-col>
      </template>
      <template v-else>
        <AppShowFeedback
          ref="dialogFeedback"
          in-dialog
          full-width
          class="mb-4"
        />
        <AppBillingContactForm
          :form.sync="form"
          :state="{
            v$,
            loading: $apollo.queries.initBillingContactForm.loading,
            validationErrors,
            fieldDisabled,
            formFields: initBillingContactForm.formFields,
            personId,
            forceCols,
            personContacts
          }"
          @close-edit-form="() => (editMode = false)"
          @set-contact="handleSetContact"
          @set-form="contact => setForm(contact)"
          @submitted-contact="emitSubmittedContact"
        />
      </template>
    </v-col>
  </div>
</template>
<script>
import gql from "fraql";
import { defineComponent } from "vue";
import { clone } from "lodash-es";
import appFormMixin from "@/mixins/appFormMixin";
import addressFormat from "@/utils/addressFormat";
import { setDefaultValuesFromFormField } from "@/utils/inputs/set_default_values";
import getObjectById from "@/utils/inputs/get_object_by_id";

import AppTableHeader from "@elements/AppTableHeader.vue";
import AppBillingContactForm from "./AppBillingContactForm.vue";
import AppShowFeedback from "@AppShows/AppShowFeedback.vue";
const contactFields = gql`
  fragment _ on Contact {
    ${AppBillingContactForm.fragments.contactFields}
    personId
    contactType
    contactableType
    name
    firstName
    lastName
    
    addressText
    addressHouse
    addressIndex
    addressCity
    addressBox
    addressState
    addressCountryId
    addressCountry @client {
      id
      name
      ioc
      alpha2
    }

    registryCountryId
    registryCountry @client {
      id
      name
      ioc
      alpha2
    }

    organisationName
    vatCode
    legalFormId
  }
`;

export default defineComponent({
  name: "AppBillingContact",
  components: {
    AppTableHeader,
    AppBillingContactForm,
    AppShowFeedback
  },
  mixins: [appFormMixin],
  props: {
    contactObject: {
      type: Object,
      default: null
    },
    personId: {
      type: String,
      default: null
    },
    contactId: {
      type: String,
      default: null
    },
    contactType: {
      type: String,
      default: null
    },
    contactableType: {
      type: String,
      required: true
    },
    contactableId: {
      type: String,
      default: null
    },
    forceCols: {
      type: Number,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    contentPadding: {
      type: String,
      default: undefined
    }
  },
  emits: [
    "set-contact", // This simply emits contact date to parent. Nothing special
    "edit-mode-state",
    "submitted-contact" // This is dedicated emit to have parent component save contact data right away
  ],
  setup() {
    return {
      ...appFormMixin.setup()
    };
  },
  data() {
    return {
      initFormInitialized: false,
      editModeValue: false,
      form: {},
      contact: null,
      initBillingContactForm: {
        vuelidateValidations: {},
        readOnlyFields: [],
        formFields: [],
        personContacts: [],
        submitNotAllowed: false
      },
      appFormMixinValidationsConfVariable: "vuelidateValidations",
      appFormMixinReadOnlyFieldsVariable:
        "initBillingContactForm.readOnlyFields",
      appFormMixinValidationsMapToModel: {
        // map API field names to form field names if they are different
        registryCountryId: "registryCountry",
        addressCountryId: "addressCountry"
      }
    };
  },
  computed: {
    vuelidateValidations() {
      if (this.submitNotAllowed) return {};
      return this.initBillingContactForm.vuelidateValidations;
    },
    submitNotAllowed() {
      return this.initBillingContactForm.submitNotAllowed || this.disabled;
    },
    personContacts() {
      return this.initBillingContactForm.personContacts ?? [];
    },
    editMode: {
      get() {
        return this.editModeValue;
      },
      set(v) {
        if (v == false && this.$el.getBoundingClientRect().top < 0) {
          this.$el.scrollIntoView();
        }
        this.editModeValue = v;
        this.$emit("edit-mode-state", v);
      }
    },
    editButtonAttrs() {
      return {
        renderable: () =>
          !this.submitNotAllowed && !this.disabled && this.editMode == false,
        "data-test": "editContact",
        class: "green-edit",
        small: true,
        icon: true,
        "aria-label": this.$t("common:editButton.alt"),
        disabled: this.$apollo.queries.initBillingContactForm.loading,
        on: {
          click: this.openEditForm
        }
      };
    }
  },
  created() {
    if (this.contactObject && !this.form.id) {
      this.setForm(this.contactObject);
    }
  },
  apollo: {
    initBillingContactForm: {
      query: gql`
        query initBillingContactForm(
          $id: ID
          $ownerPersonId: ID
          $contactableId: ID
          $contactableType: String!
          $contactType: String
        ) {
          initBillingContactForm(
            id: $id
            ownerPersonId: $ownerPersonId
            contactableId: $contactableId
            contactableType: $contactableType
            contactType: $contactType
          ) {
            contact {
              id
              ${contactFields}
            }
            personContacts {
              id
              ${contactFields}
            }
            submitNotAllowed
            vuelidateValidations
            readOnlyFields
            formFields {
              name
              defaultValue
            }
          }
        }
      `,
      variables() {
        return {
          id: this.contactId,
          ownerPersonId: this.personId,
          contactableId: this.contactableId,
          contactableType: this.contactableType,
          contactType: this.form.contactType || this.contactType
        };
      },
      manual: true,
      result({ data, loading }) {
        this.initBillingContactForm = data?.initBillingContactForm;
        // If edit form is open, skip the whole thing
        if (loading || !data || this.editMode == true) return;

        const { initBillingContactForm: initForm } = data;

        let c = initForm.contact ?? {};

        if (!initForm.contact?.id && !this.initFormInitialized) {
          const defaultContactField = initForm.formFields.find(
            x => x.name == "basedOnContactId"
          );
          if (defaultContactField && defaultContactField.defaultValue) {
            c = clone(
              initForm.personContacts.find(
                x => x.id == defaultContactField.defaultValue
              ) ?? {}
            );
            c.basedOnContactId = defaultContactField.defaultValue;
          }
        }

        this.setForm(c);
        this.contact = clone(this.form);

        this.initFormInitialized = true;
        this.$emit("set-contact", this.contact); // For EntryFeiBillingContact and EntryFeiHorseSelect

        if (c.contactType != initForm.contact?.contactType) {
          // If contactType from personContacts is different than what server returned in contact object,
          // then stop here as new initForm query will be triggered over contactType change
          return;
        }

        this.$nextTick().then(async () => {
          if (!initForm.submitNotAllowed && !this.disabled) {
            // If submit is allowed, then validate form
            // If invalid, then open edit form automatically
            //this.v$.form.$touch();
            await this.v$.form.$validate();
            // keep form open or open if $invalid is true
            this.editMode = this.editMode ? true : this.v$.form.$invalid;

            if (this.editMode) {
              this.setDefaults();
            }
            if (!this.v$.form.$invalid && !this.editMode) {
              // If its not invalid, then emit submitted-contact. For InvoiceDetails to allow saving it
              this.$emit("submitted-contact", this.form);
            }
          }
        });
      }
    }
  },
  methods: {
    /**
     * Method created for parent component to call if this contact form is valid.
     * Give returnContactAndCloseForm true boolean will return contact data if its valid and edit form is open
     * @param {boolean} returnContactAndCloseForm
     * @returns boolean, object
     */
    validateContactFormManually({
      returnContactAndCloseForm = false, // return contact details and close form
      emitContactAndCloseForm = false, // emit contact details instead of returning it and close from
      doNotAlertAutomatically = false // validate but do not scroll into right away
    }) {
      if (!this.v$.form) return true;

      if (this.editMode == true) this.$refs.dialogFeedback.close();
      this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        this.editMode = true;
        if (doNotAlertAutomatically == false) {
          this.$el.scrollIntoView();
          this.$nextTick(() => {
            this.$refs.dialogFeedback.open("warn", {
              text: this.$t("common:reviewInput.text", "Please review inputs")
            });
          });
        }

        return false;
      }

      if (this.editMode == true) {
        if (returnContactAndCloseForm) {
          this.contact = this.form;
          this.editMode = false;

          return this.contact;
        } else if (emitContactAndCloseForm) {
          this.handleSetContact(this.form);
          return true;
        }
      }

      return true;
    },
    openOrCloseEditForm(v) {
      // used in parent component
      this.editMode = v;
    },
    addressContactFormat(contact) {
      return addressFormat({
        address: contact.addressText,
        houseNumber: contact.addressHouse,
        box: contact.addressBox,
        index: contact.addressIndex,
        city: contact.addressCity,
        country: contact.addressCountry?.name
      });
    },
    validationErrors(fieldName) {
      return this.appFormMixinValidationErrorsForField(fieldName);
    },
    fieldDisabled(fieldName) {
      return this.appFormMixinIsFieldDisabled(fieldName);
    },
    setForm(c) {
      if (!c) c = {};

      this.$set(this, "form", {
        id: c.id ?? null,
        basedOnContactId: c.basedOnContactId ?? null, // not from server
        personId: c.personId ?? this.personId,
        contactType: c.contactType || this.form.contactType || "PERSON",
        contactableType: c.contactableType ?? this.contactableType,

        firstName: c.firstName ?? null,
        lastName: c.lastName ?? null,
        storeAsPersonContact: false,
        organisationName: c.organisationName ?? null,
        registryCountryId: c.registryCountryId ?? null,
        registryCountry: c.registryCountry ?? null,
        vatCode: c.vatCode ?? null,

        addressText: c.addressText ?? null,
        addressHouse: c.addressHouse ?? null,
        addressBox: c.addressBox ?? null,
        addressCity: c.addressCity ?? null,
        addressIndex: c.addressIndex ?? null,
        addressState: c.addressState ?? null,
        addressCountryId: c.addressCountryId ?? null,
        addressCountry: c.addressCountry ?? null,
        billingEmail: c.billingEmail ?? null,

        legalFormId: c.legalFormId ?? null
      });
    },
    setDefaults() {
      if (!this.form.id) {
        setDefaultValuesFromFormField(
          this.form,
          this.initBillingContactForm.formFields,
          {
            validationObject: this.v$.form
          }
        );
      }

      if (this.form.registryCountryId && !this.form.registryCountry) {
        this.form.registryCountry = getObjectById(
          this.form.registryCountryId,
          "Country"
        );
      }

      if (this.form.addressCountryId && !this.form.addressCountry) {
        this.form.addressCountry = getObjectById(
          this.form.addressCountryId,
          "Country"
        );
      }
    },
    openEditForm() {
      this.setForm(this.contact);
      this.setDefaults();
      this.editMode = true;
    },
    handleSetContact(contact) {
      this.contact = contact;
      this.$emit("set-contact", contact);
      this.editMode = false;
    },
    emitSubmittedContact(contact) {
      if (!this.disabled) {
        this.$emit("submitted-contact", contact);
      }
    }
  },
  fragments: {
    contactFields: gql`
      fragment _ on Contact {
        ${contactFields}
      }
    `
  }
});
/**
 * Removed unnecessary properties from contact object that ContactForBillingInput dislikes receiving it
 * @param {Object} contact Contact type.
 */
export function prepContactForBillingInput(contact) {
  if (!contact || typeof contact != "object") return null;
  contact = clone(contact);
  delete contact.personId;
  delete contact.name;
  delete contact.registryCountry;
  delete contact.addressCountry;
  delete contact.__typename;
  return contact;
}

/**
 * Check if emitted contact data is same as existing data in form
 * Used in InvoiceForm to prevent redundant mutation call
 * @param {Object null} currentContact Contact type from outside of AppBillingContact
 * @param {Object} newContact Contact type emitted form AppBillingContact
 */
export function contactDataIsSame(currentContact, newContact) {
  if (currentContact == undefined) return false;
  return [
    "firstName",
    "lastName",
    "organisationName",
    "registryCountryId",
    "vatCode",
    "addressText",
    "addressHouse",
    "addressCity",
    "addressIndex",
    "addressBox",
    "addressState",
    "addressCountryId",
    "billingEmail",
    "legalFormId"
  ].every(k => {
    return currentContact[k] === newContact[k];
  });
}
</script>
